/**
 * 格式化工具
 * A股配色惯例：红涨绿跌（#f44336红=上涨, #4caf50绿=下跌, #9e9e9e灰=平盘）
 */

/** A股颜色常量 */
export const STOCK_COLORS = {
  UP: '#f44336',       // 红色 = 上涨
  DOWN: '#4caf50',     // 绿色 = 下跌
  FLAT: '#9e9e9e',    // 灰色 = 平盘
  UP_BG: '#ffebee',    // 上涨背景色
  DOWN_BG: '#e8f5e9',  // 下跌背景色
} as const;

/**
 * 根据涨跌幅获取颜色
 * 正数=红(涨)，负数=绿(跌)，零=灰(平)
 */
export function getPriceColor(value: number): string {
  if (value > 0) return STOCK_COLORS.UP;
  if (value < 0) return STOCK_COLORS.DOWN;
  return STOCK_COLORS.FLAT;
}

/**
 * 格式化百分比（保留2位小数，带正负号）
 */
export function formatPct(value: number | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null || isNaN(value)) return '--';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * 格式化金额（带 ¥ 符号，千分位）
 */
export function formatMoney(value: number | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null || isNaN(value)) return '¥--';
  return `¥${value.toLocaleString('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * 格式化数字（千分位，无符号）
 */
export function formatNumber(value: number | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null || isNaN(value)) return '--';
  return value.toLocaleString('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * 格式化成交量（万手/亿手）
 */
export function formatVolume(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return '--';
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(2)}亿手`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(2)}万手`;
  }
  return `${value.toFixed(0)}手`;
}

/**
 * 格式化成交额（万元/亿元）
 */
export function formatAmount(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return '--';
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(2)}亿`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(2)}万`;
  }
  return value.toFixed(2);
}

/**
 * 获取涨跌符号前缀
 */
export function getChangePrefix(value: number): string {
  if (value > 0) return '+';
  return '';
}

/**
 * 格式化日期时间（YYYY-MM-DD HH:mm:ss）
 */
export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 格式化日期（YYYY-MM-DD）
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
